import { useState } from 'react';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { useCookiePrefAsync, useSessionAsync } from '#src/app/shared/hooks/session';
import { isLoaded } from '#src/app/shared/utils/async';
import { isAndroidPhone, isIPhone, isMobile } from '#src/app/shared/utils/userAgent';

type BannerDeviceType = 'iphone' | 'android' | 'otherMobile' | 'desktop' | 'unknown';

type Result = ModelHookResult<
	{
		deviceType: BannerDeviceType;
		bannerVisible: boolean;
	},
	{ setBannerClosed: () => void }
>;

const APP_DOWNLOAD_BOTTOM_BANNER_COOKIE = 'app_download_banner_closed_count';
const APP_DOWNLOAD_BOTTOM_BANNER_COOKIE_EXPIRATION_DAYS = 365;

function getBannerClosedCount(cookies: ReturnType<typeof useUtilityContext>['cookies']) {
	const dismissedCountStr = cookies.get(APP_DOWNLOAD_BOTTOM_BANNER_COOKIE);
	let dismissedCount: number;
	try {
		const parsed = JSON.parse(dismissedCountStr || '0') as unknown;
		dismissedCount = typeof parsed === 'number' ? parsed : 0;
	} catch (e) {
		dismissedCount = 0;
	}
	return dismissedCount;
}

function getDeviceType(utilityContext: ReturnType<typeof useUtilityContext>): BannerDeviceType {
	if (isIPhone(utilityContext)) return 'iphone';
	if (isAndroidPhone(utilityContext)) return 'android';
	if (isMobile(utilityContext)) return 'otherMobile';
	return 'desktop';
}

export function useBanner(): Result {
	const utilityContext = useUtilityContext();
	const { cookies } = utilityContext;

	// user agent AND cookies are both request-derived data that isn't available on a first render
	// served from the server-side cache (no session at render time) - reading either one then
	// throws, so both are only read once the session has loaded. When the session IS available at
	// first render (the common, non-cached case), this resolves synchronously - including during
	// SSR - so the banner renders correctly with no client-side flash.
	const userAgentDataAvailable = isLoaded(useSessionAsync());

	const deviceType = userAgentDataAvailable ? getDeviceType(utilityContext) : 'unknown';

	// Banner is disabled if the user has closed the banner 2 times or if the user is on a
	// mobile device that is not an iPhone or Android phone
	const dismissedByDefault =
		userAgentDataAvailable && (getBannerClosedCount(cookies) >= 2 || deviceType === 'otherMobile');

	const [dismissedByUser, setDismissedByUser] = useState(false);

	const cookieAllowedState = useCookiePrefAsync('preferences');
	const cookieAllowed = isLoaded(cookieAllowedState) ? cookieAllowedState.value : false;

	return {
		data: {
			deviceType,
			bannerVisible: userAgentDataAvailable && !dismissedByDefault && !dismissedByUser,
		},
		actions: {
			setBannerClosed: () => {
				setDismissedByUser(true);

				if (!cookieAllowed) return;

				cookies.set(APP_DOWNLOAD_BOTTOM_BANNER_COOKIE, JSON.stringify(getBannerClosedCount(cookies) + 1), {
					expires: APP_DOWNLOAD_BOTTOM_BANNER_COOKIE_EXPIRATION_DAYS,
					path: '/',
				});
			},
		},
	};
}
